import { computed, inject } from '@angular/core';
import {
  lineOptionItems,
  relationsConfig,
} from '@bizzdesign/modeler/utils-modeler';
import { type BDRelationMenuItem } from '@bizzdesign/modeler/models';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { ModelerLocalStore } from '../modeler/modeler.store';

export type RelationState = {
  lineOptions: BDRelationMenuItem[];
  recommendedIds: string[];
};

const initialState: RelationState = {
  lineOptions: lineOptionItems,
  recommendedIds: [],
};

export const RelationLocalStore = signalStore(
  { protectedState: false },
  withState(initialState),
  withComputed(({ lineOptions, recommendedIds }) => {
    const modelerStore = inject(ModelerLocalStore);

    const options = computed(() => {
      const metamodelName =
        modelerStore.metamodelMetadata().currentMetamodelName;
      if (!metamodelName || !relationsConfig[metamodelName]) {
        return [];
      }

      return relationsConfig[metamodelName];
    });

    const recommended = computed(() =>
      options().filter((relationOption) =>
        recommendedIds().includes(relationOption.id ?? ''),
      ),
    );
    const other = computed(() =>
      options().filter(
        (relationOption) => !recommendedIds().includes(relationOption.id ?? ''),
      ),
    );

    const categories = computed(() => ({
      recommended: recommended(),
      line: lineOptions(),
      relation: other(),
    }));

    const allMenuItems = computed(() => {
      const hasRecommended = recommended().length > 0;

      if (hasRecommended) {
        return [];
      }

      return Object.values(categories()).flatMap((menuItems) => menuItems);
    });

    return { categories, allMenuItems, options };
  }),
  withMethods((store) => {
    return {
      setRecommendedIds: (ids: string[]) => {
        patchState(store, {
          recommendedIds: [...ids],
        });
      },
    };
  }),
);

export type RelationLocalStore = InstanceType<typeof RelationLocalStore>;
